/**
 * Team room — a workspace-scoped home for an admin to run the players of the
 * workspace they're currently in (selected in the sidebar workspace selector).
 *
 * Reached from Sidebar → Workspace → "Team room". Everything on this page is
 * scoped to UseSelectedWorkspace(); switching the workspace in the sidebar
 * re-scopes the page.
 *
 * Layout: workspace identity on top, then a toolbar with the tabs on the LEFT
 * and search on the RIGHT (same shape as the Player Management toolbar).
 *
 * Tabs:
 *   - Players: a clean roster of this workspace's players (borrows Player
 *     Management's look, not its functionality).
 *   - Alerts:  the existing Performance Alerts feature, scoped to this
 *     workspace.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import SoftBox from "../../components/SoftBox";
import { Grid } from "@mui/material";
import PageHeader from "../../components/PageHeader";

import { UseSelectedWorkspace, UseCompanyColors } from "../../hooks/company";
import { GET_IMAGE } from "../../helpers/images";
import { ReactComponent as SearchSvg } from "../../img/search.svg";

import PlayersTab from "./PlayersTab";
import Automations from "../Automations";

const FONT = "DM Sans, Inter, sans-serif";

const TABS = [
  { value: "players", label: "Players" },
  { value: "alerts", label: "Automations" },
  // "Milestones & bonuses" retired 2026-08-04: pay is configured on the cards
  // themselves (Customize → the card's gear → Setup), where the real card is
  // the live preview instead of a stage imitating it. BonusesTab.jsx is kept
  // until the dev has migrated any deep links.
];

export default function TeamRoom() {
  const workspace = UseSelectedWorkspace();
  const { primaryColor } = UseCompanyColors() || {};
  const primary = primaryColor || "#1B598E";

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const activeTab = TABS.some((t) => t.value === rawTab) ? rawTab : "players";

  const [search, setSearch] = useState("");

  const workspaceId = workspace?._id || workspace?.id;
  const workspaceName = workspace?.name || "Your workspace";

  const setTab = (value) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  // Open at the TOP. The page was landing mid-scroll: the app's scroll
  // container is an ancestor Box (not the window), so it kept the offset from
  // wherever you came from, and tall tab content left it there. Runs before
  // paint on mount and on every tab switch, walking up to whichever ancestor
  // actually scrolls.
  const rootRef = useRef(null);
  useLayoutEffect(() => {
    let el = rootRef.current?.parentElement;
    while (el) {
      const canScroll = el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowY);
      if (canScroll) {
        el.scrollTop = 0;
        break;
      }
      el = el.parentElement;
    }
    window.scrollTo(0, 0); // whichever of the two is the real scroller
  }, [activeTab]);

  // Tab content can arrive a beat after mount (roster/KPI fetches) and grow the
  // page; re-assert the top once it has settled so we never land mid-page.
  useEffect(() => {
    const id = setTimeout(() => {
      let el = rootRef.current?.parentElement;
      while (el) {
        if (el.scrollTop > 0 && /(auto|scroll)/.test(getComputedStyle(el).overflowY)) {
          el.scrollTop = 0;
          return;
        }
        el = el.parentElement;
      }
      if (window.scrollY > 0) window.scrollTo(0, 0);
    }, 120);
    return () => clearTimeout(id);
  }, [activeTab]);

  return (
    <>
      <PageHeader headerText="Team room" subheaderText="See and run the players in this workspace." />
      <SoftBox ref={rootRef} sx={{ margin: { mini: "1rem", sm: "1.5rem" } }}>
        <Grid container>
          <Grid item mini={12} className="rounded-lg p-4" style={{ backgroundColor: "white" }}>
            {/* Workspace identity */}
            <SoftBox sx={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "20px" }}>
              {workspace?.image ? (
                <SoftBox
                  component="img"
                  src={GET_IMAGE(workspace.image)}
                  alt={workspaceName}
                  sx={{ width: 42, height: 42, borderRadius: "11px", objectFit: "cover" }}
                />
              ) : (
                <SoftBox
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: "11px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontFamily: FONT,
                    fontSize: "17px",
                    fontWeight: 600,
                    color: primary,
                    background: `${primary}1A`,
                  }}
                >
                  {(workspaceName || "W").charAt(0).toUpperCase()}
                </SoftBox>
              )}
              <SoftBox>
                <SoftBox sx={{ fontFamily: FONT, fontSize: "12px", color: "#98A2B3" }}>Workspace</SoftBox>
                <SoftBox sx={{ fontFamily: FONT, fontSize: "20px", fontWeight: 600, color: "#191D23" }}>
                  {workspaceName}
                </SoftBox>
              </SoftBox>
            </SoftBox>

            {/* Toolbar: tabs left, search right */}
            <SoftBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                flexWrap: "wrap",
                marginBottom: "20px",
              }}
            >
              {/* Segmented pill tabs — same language as the app's other tables */}
              <SoftBox
                sx={{
                  display: "inline-flex",
                  background: "#FFFFFF",
                  border: "1px solid #E4E7EC",
                  borderRadius: "10px",
                  padding: "3px",
                  gap: "2px",
                  height: "40px",
                  alignItems: "center",
                }}
              >
                {TABS.map((opt) => {
                  const on = activeTab === opt.value;
                  return (
                    <SoftBox
                      key={opt.value}
                      component="button"
                      type="button"
                      onClick={() => setTab(opt.value)}
                      sx={{
                        height: "32px",
                        padding: "0 16px",
                        borderRadius: "7px",
                        border: 0,
                        cursor: "pointer",
                        fontFamily: FONT,
                        fontSize: "12.5px",
                        fontWeight: 500,
                        background: on ? primary : "transparent",
                        color: on ? "#FFFFFF" : "#667085",
                        transition: "background 140ms ease, color 140ms ease",
                        "&:hover": { color: on ? "#FFFFFF" : "#191D23" },
                      }}
                    >
                      {opt.label}
                    </SoftBox>
                  );
                })}
              </SoftBox>

              {/* Search — only relevant on the Players tab */}
              {activeTab === "players" && (
                <SoftBox
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: "280px",
                    maxWidth: "100%",
                    height: "40px",
                    padding: "0 12px",
                    background: "#FFFFFF",
                    border: "1px solid #E4E7EC",
                    borderRadius: "10px",
                  }}
                >
                  <SoftBox component={SearchSvg} sx={{ width: 16, height: 16, flexShrink: 0, "& path": { stroke: "#98A2B3" } }} />
                  <SoftBox
                    component="input"
                    type="text"
                    placeholder="Search players"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                      flex: 1,
                      border: 0,
                      outline: "none",
                      background: "transparent",
                      fontFamily: FONT,
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#191D23",
                      "&::placeholder": { color: "#98A2B3" },
                    }}
                  />
                </SoftBox>
              )}
            </SoftBox>

            {/* Tab content */}
            {activeTab === "players" && <PlayersTab workspaceId={workspaceId} search={search} />}
            {activeTab === "alerts" && <Automations workspaceId={workspaceId} />}
          </Grid>
        </Grid>
      </SoftBox>
    </>
  );
}
